import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Phone, MessageCircle, Mail, MapPin, CreditCard, ExternalLink, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany, toTelHref, toWhatsAppHref, toMapsHref, fullAddress, formatHours, DAY_KEYS, DAY_LABELS_SHORT } from "@/lib/useCompany";
import { useSiteContent } from "@/lib/useSiteContent";
import { useI18n } from "@/i18n/context";

const FALLBACK_QUICK = [
  { to: "/reparatur", label: "Reparatur starten" },
  { to: "/preise", label: "Preise" },
  { to: "/termin-buchen", label: "Termin buchen" },
  { to: "/reparaturstatus", label: "Reparaturstatus" },
  { to: "/versand-reparatur", label: "Versand-Reparatur" },
  { to: "/standorte", label: "Standorte" },
  { to: "/faq", label: "FAQ" },
];
const FALLBACK_LEGAL = [
  { to: "/impressum", label: "Impressum" },
  { to: "/datenschutzerklaerung", label: "Datenschutzerklärung" },
  { to: "/agb", label: "AGB" },
  { to: "/reparaturbedingungen", label: "Reparaturbedingungen" },
  { to: "/widerruf", label: "Widerrufsbelehrung" },
  { to: "/cookie-einstellungen", label: "Cookie-Einstellungen" },
  { to: "/datenschutz", label: "Datenschutz bei Reparaturen" },
  { to: "/garantie", label: "Garantie" },
];

export default function Footer() {
  const c = useCompany();
  const site = useSiteContent();
  const { lang, t } = useI18n();
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const load = () => api.get("/footer-links").then(r => setLinks(r.data)).catch(() => {});
    load();
    const onUpd = () => load();
    window.addEventListener("handymeister:data-updated", onUpd);
    window.addEventListener("focus", onUpd);
    return () => {
      window.removeEventListener("handymeister:data-updated", onUpd);
      window.removeEventListener("focus", onUpd);
    };
  }, []);

  const groups = links.reduce((acc, l) => { (acc[l.group] = acc[l.group] || []).push(l); return acc; }, {});
  const popularRepairs = Array.isArray(site.footer_popular_repairs_items) ? site.footer_popular_repairs_items : [];
  const translatedLabel = (item) => {
    if (lang === "de") return item.label;
    const keyByPath = {
      "/": "home", "/reparatur": "repair", "/preise": "prices", "/termin-buchen": "appointment",
      "/reparaturstatus": "status", "/versand-reparatur": "shipping", "/standorte": "locations",
      "/ueber-uns": "about", "/faq": "faq", "/kontakt": "contact",
    };
    const key = keyByPath[item.href || item.to];
    return key ? t(`nav.${key}`) : item.label;
  };


  const logoText = c.company_name || "Handy Meister";
  const parts = logoText.split(" ");
  const first = parts[0];
  const rest = parts.slice(1).join(" ");

  return (
    <footer className="mt-24 border-t border-white/8 bg-[#131316]" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-display text-xl font-semibold tracking-tight">
              {first} {rest && <span className="text-[#0055FF]">{rest}</span>}
            </div>
            <div className="section-label mt-1">{site.footer_smartphone_label}</div>
            <p className="mt-5 text-sm text-zinc-400 leading-relaxed max-w-sm">
              {site.footer_tagline}
            </p>

            <div className="mt-6 space-y-2 text-sm">
              <a href={toTelHref(c.phone)} className="flex items-center gap-2 text-zinc-100 hover:text-[#0055FF]" data-testid="footer-phone">
                <Phone size={15}/> {c.phone}
              </a>
              <a href={toWhatsAppHref(c.whatsapp)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-zinc-100 hover:text-emerald-400" data-testid="footer-whatsapp">
                <MessageCircle size={15}/> {c.whatsapp}
              </a>
              <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-zinc-100 hover:text-[#0055FF]" data-testid="footer-email">
                <Mail size={15}/> {c.email}
              </a>
              <div className="flex items-center gap-2 text-zinc-100" data-testid="footer-address">
                <MapPin size={15}/> {fullAddress(c)}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="section-label">{site.footer_quick_links_heading}</div>
            <ul className="mt-4 space-y-2" data-testid="footer-quick-links">
              {(groups["Service"] || groups[Object.keys(groups)[0]] || FALLBACK_QUICK).map((q) => (
                <li key={q.id || q.to}>
                  <Link to={q.href || q.to} className="text-sm text-zinc-300 hover:text-[#0055FF]" data-testid={`footer-link-${(q.href || q.to).replace(/[/]/g,'-')}`}>{translatedLabel(q)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="section-label">{site.footer_popular_repairs_heading}</div>
            <ul className="mt-4 space-y-2">
              {popularRepairs.map((r, i) => (
                <li key={`${r}-${i}`} className="text-sm text-zinc-300">{r}</li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="section-label">{site.footer_legal_heading}</div>
            <ul className="mt-4 space-y-2" data-testid="footer-legal-links">
              {(groups["Rechtliches"] || FALLBACK_LEGAL).map((l) => (
                <li key={l.id || l.to}>
                  <Link to={l.href || l.to} className="text-sm text-zinc-300 hover:text-[#0055FF]">{translatedLabel(l)}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="section-label">{site.footer_hours_heading}</div>
            <ul className="mt-4 space-y-1.5 text-sm text-zinc-300 tabular-nums" data-testid="footer-hours">
              {DAY_KEYS.map((k) => (
                <li key={k} className="flex justify-between gap-2">
                  <span>{DAY_LABELS_SHORT[k]}</span>
                  <span className={c.opening_hours?.[k]?.closed ? "text-zinc-500" : ""}>
                    {formatHours(c.opening_hours?.[k])}
                  </span>
                </li>
              ))}
            </ul>
            {c.special_notes && (
              <p className="mt-3 text-[11px] text-amber-400/80" data-testid="footer-special-notes">{c.special_notes}</p>
            )}
            {c.social_links && Object.values(c.social_links).some(Boolean) && (
              <>
                <div className="section-label mt-6">{site.footer_social_heading}</div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="footer-social">
                  {Object.entries(c.social_links).filter(([, url]) => url).map(([platform, url]) => (
                    <a key={platform} href={url} target="_blank" rel="noreferrer"
                      className="px-3 py-1 rounded-full text-xs bg-white/[0.06] text-zinc-300 hover:bg-white/[0.1] hover:text-white capitalize"
                      data-testid={`footer-social-${platform}`}>
                      {platform}
                    </a>
                  ))}
                </div>
              </>
            )}
            <div className="section-label mt-6">{site.footer_payment_heading}</div>
            <div className="mt-3 flex items-center gap-2 text-zinc-500">
              <CreditCard size={16}/>
              <span className="text-xs">{site.footer_payment_methods}</span>
            </div>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="premium-card p-5 flex items-start gap-3">
            <ShieldCheck size={20} className="text-[#4d9aff] mt-0.5"/>
            <div><div className="font-medium">{site.home_trust_title}</div><p className="mt-1 text-xs text-zinc-400">{site.hero_trust_bullets?.slice(0, 3).join(" · ")}</p></div>
          </div>
          <div className="premium-card p-5">
            <div className="section-label">{site.footer_map_heading}</div>
            <div className="mt-2 text-sm text-zinc-300">{fullAddress(c)}</div>
            <a href={toMapsHref(c)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm text-[#4d9aff] hover:text-white" data-testid="footer-map-link">
              {site.footer_map_button} <ExternalLink size={13}/>
            </a>
          </div>
          <div className="premium-card p-5">
            <div className="section-label">{site.footer_payment_heading}</div>
            <div className="mt-3 flex items-center gap-2 text-zinc-300"><CreditCard size={17}/><span className="text-sm">{site.footer_payment_methods}</span></div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-zinc-500">{site.footer_copyright}</p>
          <p className="text-xs text-zinc-500" data-testid="footer-legal-name">{site.footer_made_in} · {c.legal_name}</p>
        </div>
      </div>
    </footer>
  );
}


